import { useRef, useEffect, useState } from "react";
import TreeView from "devextreme-react/tree-view";
import "./layout.scss";
import styles from "./layout.module.scss";
import * as events from "devextreme/events";
import Drawer from "devextreme-react/drawer";
import { Template } from "devextreme-react/core/template";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./header/header";
import Footer from "./footer/footer";
import { connect } from "react-redux";
import * as actions from "redux/actions/index";

function Component(props) {
  // hooks

  const treeView = useRef();
  const [divNode, setDivNode] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // event handlers

  function toggleMenu() {
    if (props.menuOpened) treeView.current.collapseAll();
    props.toggleMenu();
  }

  function selectedItem() {
    let path2 = location.pathname.split("/")[2];
    let path3 = location.pathname.split("/")[3];

    if (path2 === undefined) return "/home";

    let item = null;

    props.navigation.forEach((obj) => {
      if (obj.items && obj.items.length >= 0 && obj.items.some((item) => item.path.includes(`/${path2}`))) {
        item = obj.items.find((item) => item.path.includes(`/${path3}`)) || obj.items[0];
      }
    });
    return (item && item.path) || "/home";
  }

  function onItemClick(e) {
    if (props.menuOpened && e.itemData.path) {
      paintHighlight(e.element, e.itemElement);
      navigate(`/pages${e.itemData.path}`);
    }
  }

  // Paint highlight synchronously: selectItem inside onItemClick breaks navigation, so we mutate classes directly and let useEffect's selectItem sync DevExtreme state after.
  function paintHighlight(treeRoot, itemElement) {
    const node = itemElement?.closest?.(".dx-treeview-node");
    if (!node || !treeRoot) return;
    treeRoot.querySelectorAll(".dx-state-selected").forEach((n) => n.classList.remove("dx-state-selected"));
    node.classList.add("dx-state-selected");
    let parent = node.parentElement;
    while (parent) {
      if (parent.classList && parent.classList.contains("dx-treeview-node")) {
        parent.classList.add("dx-state-selected");
      }
      parent = parent.parentElement;
    }
  }

  function onMenu(e) {
    if (!props.menuOpened) {
      props.toggleMenu();
    }
  }

  // Collapsed, the drawer clips to a 60px icon rail and the label is cut off, so the icon
  // needs a hover tooltip. The TreeView has no per-item hint, so stash the label on the
  // rendered element and mirror it into the native title attribute -- the same mechanism
  // DevExtreme's own `hint` uses. A CSS pseudo-element would be clipped by the drawer;
  // the browser paints title outside the DOM, so it escapes.
  function onItemRendered(e) {
    e.itemElement.dataset.hint = e.itemData.text;
    e.itemElement.title = props.menuOpened ? "" : e.itemData.text;
  }

  // functions

  function highlightAncestors(element) {
    const nodeClass = "dx-treeview-node";
    const selectedClass = "dx-state-selected";
    const leafNodeClass = "dx-treeview-node-is-leaf";

    const rootNodes = element.querySelectorAll(`.${nodeClass}:not(.${leafNodeClass})`);

    Array.from(rootNodes).forEach((node) => {
      node.classList.remove(selectedClass);
    });

    let selectedNode = element.querySelector(`.${nodeClass}.${selectedClass}`);

    if (selectedNode) {
      while (selectedNode && selectedNode.parentElement) {
        if (selectedNode.classList.contains(nodeClass)) {
          selectedNode.classList.add(selectedClass);
        }

        selectedNode = selectedNode.parentElement;
      }
    }
  }

  function onSelectionChanged(e) {
    highlightAncestors(e.element);
  }

  function onContentReady(e) {
    treeView.current?.selectItem(selectedItem());
    highlightAncestors(e.element);
  }

  // Sync selection with URL (clicks, back/forward, deep links).
  useEffect(() => {
    treeView.current?.selectItem(selectedItem());
  }, [location.pathname]);

  // Toggling the drawer does not re-render the items, so refresh the titles here. Clearing
  // them while open keeps the tooltip from repeating a label already on screen.
  useEffect(() => {
    if (!divNode) return;
    divNode.querySelectorAll("[data-hint]").forEach((el) => {
      el.title = props.menuOpened ? "" : el.dataset.hint;
    });
  }, [divNode, props.menuOpened]);

  // Bind the menu-open click handler once the drawer template has mounted the div.
  useEffect(() => {
    if (!divNode) return;
    events.on(divNode, "dxclick", onMenu);
    return () => events.off(divNode, "dxclick");
  }, [divNode, props.menuOpened]);

  return (
    <div className={styles.main}>
      <div className={styles.header}>
        <Header menuToggleEnabled toggleMenu={toggleMenu} />
      </div>
      <Drawer
        className={"pages_layout_side"}
        opened={props.menuOpened}
        openedStateMode={"shrink"}
        revealMode={"expand"}
        shading={false}
        minSize={60}
        position={"before"}
        maxSize={200}
        template={"menu"}
      >
        <div className={styles.container}>{props.children}</div>

        <Template name="menu">
          <div ref={setDivNode} className={`pages_layout_side_treeview ${styles.tree}`}>
            <div className={styles.tree_item}>
              <TreeView
                items={props.navigation}
                selectionMode={"single"}
                keyExpr={"path"}
                searchMode={"contains"}
                expandEvent={"click"}
                width={"100%"}
                onSelectionChanged={onSelectionChanged}
                onItemClick={onItemClick}
                onItemRendered={onItemRendered}
                onInitialized={(e) => {
                  treeView.current = e.component;
                  treeView.current.option("deferRendering", false);
                }}
                onContentReady={onContentReady}
              />
            </div>
          </div>
        </Template>
      </Drawer>
      <div className={styles.footer}>
        <Footer />
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    menuOpened: state.ui.menuOpened,
    user: state.user,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    toggleMenu: () => dispatch(actions.toggleMenu()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Component);
